import { useEffect, useRef, useState } from 'react'
import type { VideoCodec } from 'livekit-client'
import { AppWindow, Lock, Monitor, MonitorPlay } from 'lucide-react'
import { Modal } from '@/components/settings/Modal'
import { cx } from '@/lib/cx'
import { desktop, desktopPlatform, type DesktopSource } from '@/lib/desktop'
import { systemAudioAvailable } from '@/lib/audio/systemAudio'
import { SHARE_FPS, SHARE_HEIGHT, SHARE_RESOLUTIONS, clampShareQuality, shareCaps, shareLabel, shareUnlock, type ScreenShareQuality, type ShareFps, type ShareResolution } from '@/lib/screenShare'
import { autoVideoCodec, screenEncoding, VIDEO_CODEC_CHOICES } from '@/lib/videoCodec'
import { watchSources } from '@/lib/sourceWatcher'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import styles from './ScreenShareModal.module.css'

interface Props {
  initial: ScreenShareQuality
  /** Сервер, на котором показываем: его уровень поднимает потолок качества. В ЛС — без сервера. */
  guildId?: string | null
  /** title — название выбранного окна; у экрана своего названия нет, там будет «Экран <ник>». */
  onStart: (quality: ScreenShareQuality, title?: string, testCodec?: VideoCodec) => Promise<void> | void
  onClose: () => void
}

type Kind = 'window' | 'screen'

const TABS: { value: Kind; label: string; icon: typeof AppWindow }[] = [
  { value: 'window', label: 'Приложения', icon: AppWindow },
  { value: 'screen', label: 'Экраны', icon: Monitor },
]

/**
 * Что показать и с каким качеством — одним окном, как в Discord.
 *
 * В приложении список экранов и окон даёт оболочка, и выбранное уходит ей же: системный диалог
 * Chromium не появляется вовсе. В браузере списка нет — остаётся только качество, а что показывать
 * спросит сам браузер.
 */
export function ScreenShareModal({ initial, guildId, onStart, onClose }: Props) {
  const plus = useAuthStore((s) => s.user?.premium_type === 2)
  const tier = useGuildStore((s) => (guildId ? (s.guilds[guildId]?.premium_tier ?? 0) : 0))
  const caps = shareCaps(plus, tier)
  // считаем по самим вариантам, а не по числу: список меняется, подсказка не должна отставать
  const locked = SHARE_RESOLUTIONS.some((r) => SHARE_HEIGHT[r.value] > caps.height) || SHARE_FPS.some((f) => f.value > caps.fps)
  // сохранённый выбор мог пережить конец «Плюса» или снятие бустов — открываемся на доступном
  const allowed = clampShareQuality(initial, caps)
  const [resolution, setResolution] = useState<ShareResolution>(allowed.resolution)
  const [fps, setFps] = useState<ShareFps>(allowed.fps)
  const [testCodec, setTestCodec] = useState<'auto' | VideoCodec>('auto')
  useEffect(() => {
    void autoVideoCodec.prepare(screenEncoding({ resolution, fps, muteAudio: false }))
  }, [resolution, fps])
  const browser = desktopPlatform === ''
  const [muteAudio, setMuteAudio] = useState(initial.muteAudio)
  const [busy, setBusy] = useState(false)
  // система может отдать звук компьютера без нашего собственного; null — ещё не спросили
  const [clean, setClean] = useState<boolean | null>(null)
  /*
   * На Windows звук есть всегда: не чистым захватом, так общесистемным loopback.
   * На macOS он бывает только чистым захватом (нужна macOS 13), а Linux звука не даёт вовсе —
   * пока не ответили, галочку показываем: ответ приходит за миллисекунды.
   */
  const noSystemAudio = !browser && desktopPlatform !== 'win32' && clean === false
  useEffect(() => {
    let alive = true
    void systemAudioAvailable().then((yes) => {
      if (alive) setClean(yes)
    })
    return () => {
      alive = false
    }
  }, [])

  const canPick = Boolean(desktop?.sources)
  const [tab, setTab] = useState<Kind>('window')
  const [list, setList] = useState<DesktopSource[] | null>(null)
  const [denied, setDenied] = useState(false)
  const [picked, setPicked] = useState<string | null>(null)
  const [sourceError, setSourceError] = useState(false)
  const watcher = useRef<ReturnType<typeof watchSources> | null>(null)

  /*
   * Новые/закрытые окна и превью обновляются без сброса выбранной плитки.
   * Запросы идут последовательно; скрытый или закрытый диалог ничего не опрашивает.
   */
  useEffect(() => {
    const ask = desktop?.sources
    if (!ask) return
    const live = watchSources(ask, (res) => {
      setSourceError(false)
      setDenied(res.ok === false && res.reason === 'permission')
      const found = res.sources ?? []
      setList(found)
      // выбранное окно могли закрыть, пока окно открыто: снимаем выбор, а не стартуем в пустоту
      setPicked((id) => (id && found.some((x) => x.id === id) ? id : null))
    }, () => {
      setSourceError(true)
      setList((previous) => previous ?? [])
    })
    watcher.current = live
    return () => {
      watcher.current = null
      live.stop()
    }
  }, [])

  useEffect(() => { watcher.current?.kind(tab) }, [tab])
  useEffect(() => {
    const sync = () => watcher.current?.pause(busy || document.hidden)
    const focus = () => { sync(); if (!busy && !document.hidden) watcher.current?.refresh() }
    sync()
    document.addEventListener('visibilitychange', sync)
    window.addEventListener('focus', focus)
    return () => {
      document.removeEventListener('visibilitychange', sync)
      window.removeEventListener('focus', focus)
    }
  }, [busy])

  // передумали — оболочка не должна остаться с выбранным источником на следующий раз
  useEffect(() => () => void desktop?.pickSource?.(null), [])

  const quality: ScreenShareQuality = { resolution, fps, muteAudio }
  const chosen = list?.find((s) => s.id === picked) ?? null
  const grid = canPick && !denied
  const needsPick = grid && (list === null || list.length > 0)
  const ready = !needsPick || picked !== null

  const start = async () => {
    if (busy || !ready) return
    watcher.current?.pause(true)
    setBusy(true)
    try {
      // выбор уходит оболочке до запроса на демонстрацию, иначе она покажет своё окно
      if (grid) await desktop?.pickSource?.(picked)
      await onStart(quality, grid && chosen?.id.startsWith('window:') ? chosen.name : undefined, testCodec !== 'auto' ? testCodec : undefined)
      onClose()
    } finally {
      setBusy(false)
    }
  }

  const options = (
    <>
      <div className={styles.settings}>
        <div className={styles.column}>
          <span className={styles.label}>Разрешение</span>
          <div className={styles.chips} role="radiogroup" aria-label="Разрешение">
            {SHARE_RESOLUTIONS.map((r) => {
              // подсказку считаем на минимальной частоте: этот выбор — только про разрешение
              const need = SHARE_HEIGHT[r.value] > caps.height ? shareUnlock(SHARE_HEIGHT[r.value], 15) : null
              return (
                <button key={r.value} type="button" role="radio" aria-checked={resolution === r.value} disabled={need !== null} className={cx(styles.chip, need && styles.chipLocked)} onClick={() => setResolution(r.value)}>
                  <span className={styles.chipLabel}>{r.label}</span>
                  <span className={cx(styles.chipHint, need && styles.chipNeed)}>{need ?? r.hint}</span>
                  {need ? <Lock size={12} className={styles.chipLock} aria-hidden /> : null}
                </button>
              )
            })}
          </div>
        </div>
        <div className={styles.column}>
          <span className={styles.label}>Частота кадров</span>
          <div className={styles.chips} role="radiogroup" aria-label="Частота кадров">
            {SHARE_FPS.map((f) => {
              // и наоборот: разрешение берём минимальное, речь только про кадры
              const need = f.value > caps.fps ? shareUnlock(720, f.value) : null
              return (
                <button key={f.value} type="button" role="radio" aria-checked={fps === f.value} disabled={need !== null} className={cx(styles.chip, need && styles.chipLocked)} onClick={() => setFps(f.value)}>
                  <span className={styles.chipLabel}>{f.label}</span>
                  <span className={cx(styles.chipHint, need && styles.chipNeed)}>{need ?? f.hint}</span>
                  {need ? <Lock size={12} className={styles.chipLock} aria-hidden /> : null}
                </button>
              )
            })}
          </div>
        </div>
      </div>
      {locked ? (
        <p className={styles.optionHint}>
          Качество — по лучшему из двух: вашего Плюса и уровня сервера. Бусты поднимают его всем, кто показывает на сервере.
        </p>
      ) : null}
      <div className={styles.section}>
        <span className={styles.label}>Кодек</span>
        <div className={cx(styles.chips, styles.codecChips)} role="radiogroup" aria-label="Кодек трансляции">
          <button type="button" role="radio" aria-checked={testCodec === 'auto'} className={styles.chip} onClick={() => setTestCodec('auto')}>
            <span className={styles.chipLabel}>Авто</span>
          </button>
          {VIDEO_CODEC_CHOICES.map((codec) => {
            const supported = autoVideoCodec.supports(codec.value)
            return (
              <button key={codec.value} type="button" role="radio" aria-checked={testCodec === codec.value} disabled={!supported} className={cx(styles.chip, !supported && styles.chipLocked)} onClick={() => setTestCodec(codec.value)}>
                <span className={styles.chipLabel}>{codec.label}</span>
                {!supported ? <span className={styles.chipHint}>Недоступен</span> : null}
              </button>
            )
          })}
        </div>
        <p className={styles.optionHint}>Выбор действует только на эту трансляцию.</p>
      </div>
      <div className={styles.section}>
        <span className={styles.label}>Звук</span>
        {noSystemAudio ? (
          <p className={styles.optionHint}>Эта система не отдаёт звук программ — трансляция пойдёт без звука.</p>
        ) : (
          <>
            <label className={styles.audio}>
              <input type="checkbox" checked={muteAudio} onChange={(e) => setMuteAudio(e.target.checked)} />
              <span>
                Заглушить звук трансляции
                <span className={styles.optionHint}>По умолчанию звук компьютера идёт вместе с картинкой.</span>
              </span>
            </label>
            <p className={styles.optionHint}>
              {browser
                ? 'Голоса из канала в трансляцию не попадут. В браузере звук идёт, только если отметить его в окне выбора.'
                : clean === true
                  ? 'Голоса из канала в трансляцию не попадут: система отдаёт звук компьютера без звука самого Кличата.'
                  : 'Голоса из канала в трансляцию не попадут: мы вычитаем из звука то, что играет сам Кличат.'}
            </p>
          </>
        )}
      </div>
    </>
  )

  return (
    <Modal title="Демонстрация экрана" onClose={onClose} width={grid ? 760 : 440}>
      {grid ? (
        <div className={styles.section}>
          <div className={styles.tabsRow}>
            <div className={styles.tabs} role="tablist" aria-label="Что показать">
              {TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.value}
                  className={cx(styles.tab, tab === t.value && styles.tabActive)}
                  onClick={() => {
                    if (tab === t.value) return
                    setTab(t.value)
                    setPicked(null)
                    setList(null)
                  }}
                >
                  <t.icon size={16} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {sourceError ? <p className={styles.optionHint}>Не удалось обновить список окон. Пробуем снова…</p> : null}
          <div className={cx(styles.grid, 'scroller')} role="radiogroup" aria-label="Что показать">
            {list === null
              ? Array.from({ length: 6 }, (_, i) => <span key={i} className={styles.skeleton} />)
              : list.map((s) => (
                  <button key={s.id} type="button" role="radio" aria-checked={picked === s.id} className={cx(styles.tile, picked === s.id && styles.tilePicked)} onClick={() => setPicked(s.id)} onDoubleClick={() => void start()}>
                    <img className={styles.preview} src={s.thumbnail} alt="" draggable={false} />
                    <span className={styles.tileFoot}>
                      {s.appIcon ? (
                        <img className={styles.tileIcon} src={s.appIcon} alt="" draggable={false} />
                      ) : tab === 'window' ? (
                        <AppWindow size={14} className={styles.tileIcon} />
                      ) : (
                        <Monitor size={14} className={styles.tileIcon} />
                      )}
                      <span className={styles.tileName}>{s.name}</span>
                    </span>
                  </button>
                ))}
            {list?.length === 0 ? <p className={styles.empty}>{tab === 'window' ? 'Открытых окон не нашлось.' : 'Экранов не нашлось.'}</p> : null}
          </div>
        </div>
      ) : null}

      {denied ? (
        <p className={styles.optionHint}>
          macOS не дала доступ к записи экрана, поэтому списка окон нет. Нажмите «Начать демонстрацию» — приложение покажет, что включить в настройках.
        </p>
      ) : null}

      {options}

      <div className={styles.footer}>
        <span className={styles.summary}>
          {chosen ? <MonitorPlay size={14} className={styles.summaryIcon} /> : null}
          <span className={styles.summaryText}>
            {chosen ? `${chosen.name} · ${shareLabel(quality)}` : grid ? `${shareLabel(quality)} · выберите, что показать` : shareLabel(quality)}
          </span>
        </span>
        <button type="button" className={styles.start} disabled={busy || !ready} onClick={() => void start()}>
          {busy ? 'Запускаем…' : 'Начать демонстрацию'}
        </button>
      </div>
    </Modal>
  )
}
